import React, { useRef, useState,useEffect } from "react";
import "./Plans.scss";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/all";
import { useGSAP } from "@gsap/react";
import { FaArrowRight, FaCheck } from "react-icons/fa6";
import {
  splitTextIntoSpans,
  animateTextReveal,
  maskAnimation,
} from "../../../utils/gsapAnimations";

import AgentsVideo from "../../../assets/videos/SlightlyLargerAgents.mp4";
import LandlordVideo from "../../../assets/videos/CompressedLandlord.mp4";
import PlaceHolderAgents from "../../../assets/images/placeHolderAgent.webp";
import PlaceHolderLandlord from "../../../assets/images/LandlordPoster.webp";

export default function Plans({ onContact }) {
  const largeTitleRef = useRef(null);
  const actualLargeTitle = useRef(null);
  const normalTextRef = useRef(null);
  const selectorRef = useRef(null);
  const videoRef = useRef(null);
  const videoContainerRef = useRef(null);
  const featuresContainerRef = useRef([]);

  const [selectedPlan, setSelectedPlan] = useState("agents");
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false);

  // Plan texts
  const planTexts = {
    agents:
      "Designed for real estate agents and agencies managing multiple clients and listings. Get advanced analytics, priority support, and automation tools to streamline your workflow and maximize commissions.",
    landlord:
      "Perfect for individual landlords or property owners. Enjoy transparent pricing, essential management features, and scalable options to help you maximize rental income and simplify property oversight.",
  };

  // Video sources and posters
  const planVideos = { agents: AgentsVideo, landlord: LandlordVideo };
  const planPosters = {
    agents: PlaceHolderAgents,
    landlord: PlaceHolderLandlord,
  };

  // Split text using utility functions
  const largeTitleSpans = splitTextIntoSpans(
    "Choose the perfect plan for your property goals."
  );
  const smallTitleSpans = splitTextIntoSpans("PLANS", "");
  const normalTextSpans = splitTextIntoSpans(planTexts[selectedPlan]);

  // Split plan selector button text for animation
  const agentsBtnSpans = "Agents Plan".split("").map((char, i) => (
    <span className="plan-selector-letter" key={i}>
      {char === " " ? "\u00A0" : char}
    </span>
  ));
  const landlordBtnSpans = "Landlord Plan".split("").map((char, i) => (
    <span className="plan-selector-letter" key={i}>
      {char === " " ? "\u00A0" : char}
    </span>
  ));

  // Features arrays
  const agentFeatures = [
    "Multi-property & landlord management",
    "Commission tracking & reporting",
    "Staff management with permissions",
    "Bulk property listing tools",
    "Automated rent reminders",
    "Maintenance request tracking",
    "Financial reporting per landlord",
    "Payment integrations (M-Pesa, bank)",
    "Tenant screening & applications",
  ];

  const landlordFeatures = [
    "Manage own properties and tenants directly",
    "Lease and tenant management",
    "Automated rent invoicing & receipting",
    "Rent reminders via SMS/email",
    "Maintenance request logging & approval",
    "Payment tracking with M-Pesa/bank integration",
    "Owner dashboard for income/expense overview",
    "Landlord statements & financial reports",
    "Document storage (leases, receipts, property documents)",
    "Vacancy advertising on platform",
    "Basic tenant screening",
    "Expense tracking (repairs, utilities, etc.)",
  ];

  useGSAP(() => {
    const isMobile = window.innerWidth <= 768;

    // Get small title element from within large title
    const smallTitleElement =
      largeTitleRef.current?.querySelector(".small-title");

    // Animate small title using utility
    if (smallTitleElement) {
      animateTextReveal(smallTitleElement, {
        trigger: largeTitleRef.current,
        start: "top 90%",
        duration: 1.5,
        stagger: 0.09,
        isMobile,
      });
    }

    // // Animate large title using utility
    // animateTextReveal(largeTitleRef.current, {
    //   start: "top 90%",
    //   duration: 0.75,
    //   stagger: 0.03,
    //   isMobile,
    // });

    maskAnimation(actualLargeTitle.current, {
      trigger: largeTitleRef.current,
      start: "top 90%",
      duration: 1,
      stagger: 0.03,
    });

    // Animate selector buttons
    if (selectorRef.current) {
      const selectorBtns =
        selectorRef.current.querySelectorAll(".plan-selector-btn");
      gsap.fromTo(
        selectorBtns,
        {
          y: 40, opacity: 0,
          // filter: "blur(10px)"
        },
        {
          y: 0,
          opacity: 1,
          // filter: "blur(0px)",
          duration: isMobile ? 0.3 : 0.5,
          ease: "power1.inOut",
          stagger: isMobile ? 0.05 : 0.1,
          scrollTrigger: {
            trigger: selectorRef.current,
            start: "top 95%",
            toggleActions: "play none none reverse",
            refreshPriority: -1,
          },
        }
      );
    }
  }, []);

  // Lazy load video when container is near viewport
  useEffect(() => {
    const observer = new window.IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoadVideo(true);
          observer.disconnect();
        }
      },
      { root: null, rootMargin: "200px", threshold: 0 }
    );
    if (videoContainerRef.current) {
      observer.observe(videoContainerRef.current);
    }
    return () => observer.disconnect();
  }, []);

  // Animate video using utility-like approach
  useGSAP(() => {
    if (!shouldLoadVideo || !videoRef.current) return;
    const isMobile = window.innerWidth <= 768;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        videoRef.current,
        {
          x: 100, opacity: 0,
          // filter: "blur(10px)"
        },
        {
          x: 0,
          opacity: 1,
          // filter: "blur(0px)",
          duration: isMobile ? 0.5 : 1,
          ease: "power1.inOut",
          scrollTrigger: {
            trigger: videoRef.current,
            start: "top 95%",
            toggleActions: "play none none reverse",
            once: true,
          },
        }
      );
      ScrollTrigger.refresh();
    });

    return () => ctx.revert();
  }, [shouldLoadVideo]);

  // Animate normal text on selection change using utility
  useGSAP(() => {
    animateTextReveal(normalTextRef.current, {
      start: "top 90%",
      duration: 0.5,
      stagger: 0.03,
      isMobile: window.innerWidth <= 768,
    });
  }, [selectedPlan]);

  // Animate features with custom logic (different from standard text reveal)
  useGSAP(() => {
    if (!featuresContainerRef.current) return;
    const isMobile = window.innerWidth <= 768;
    const items = featuresContainerRef.current.querySelectorAll(
      ".feature-item:not([aria-hidden])"
    );
    if (!items.length) return;

    gsap.fromTo(
      items,
      { y: 34, opacity: 0, filter: "blur(8px)" },
      {
        y: 0,
        opacity: 1,
        filter: "blur(0px)",
        duration: isMobile ? 0.9 : 1.4,
        delay: 0.5,
        ease: "power3.out",
        stagger: isMobile ? 0.12 : 0.18,
        onComplete: () => {
          items.forEach((el) => el.removeAttribute("style"));
        },
      }
    );
  }, [selectedPlan]);

  return (
    <>
      <div className="plans-container" id="pricing">
        <div className="features-texts">
          <div className="large-title" ref={largeTitleRef}>
            <div className="small-title">{smallTitleSpans}</div>
            <span ref={actualLargeTitle}>
              {/* {largeTitleSpans} */}
              <span> Choose the perfect plan for your property goals.</span>
            </span>
          </div>
          <div className="plan-selector" ref={selectorRef}>
            <button
              className={`plan-selector-btn${
                selectedPlan === "agents" ? " active" : ""
              }`}
              onClick={() => setSelectedPlan("agents")}
            >
              {agentsBtnSpans}
            </button>
            <button
              className={`plan-selector-btn${
                selectedPlan === "landlord" ? " active" : ""
              }`}
              onClick={() => setSelectedPlan("landlord")}
            >
              {landlordBtnSpans}
            </button>
          </div>
          <div className="normal-text" ref={normalTextRef}>
            {normalTextSpans}
          </div>

          {selectedPlan === "agents" && (
            <div
              className="agent-features scrolling-features"
              ref={featuresContainerRef}
            >
              <div className="features-track">
                {agentFeatures.map((feature, index) => (
                  <div key={`a1-${index}`} className="feature-item">
                    <FaCheck className="feature-icon" />
                    <span>{feature}</span>
                  </div>
                ))}
                {agentFeatures.map((feature, index) => (
                  <div
                    key={`a2-${index}`}
                    className="feature-item"
                    aria-hidden="true"
                  >
                    <FaCheck className="feature-icon" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {selectedPlan === "landlord" && (
            <div
              className="landlord-features scrolling-features"
              ref={featuresContainerRef}
            >
              <div className="features-track">
                {landlordFeatures.map((feature, index) => (
                  <div key={`l1-${index}`} className="feature-item">
                    <FaCheck className="feature-icon" />
                    <span>{feature}</span>
                  </div>
                ))}
                {landlordFeatures.map((feature, index) => (
                  <div
                    key={`l2-${index}`}
                    className="feature-item"
                    aria-hidden="true"
                  >
                    <FaCheck className="feature-icon" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="buttons" style={{ marginTop: "2vw" }}>
            <div className="button" onClick={onContact}>
              {"Try today".split("").map((char, i) => (
                <span className="hero-button-letter" key={i}>
                  {char === " " ? "\u00A0" : char}
                </span>
              ))}
            </div>
            <div className="button-two">
              <span className="hero-button-letter">
                <FaArrowRight />
              </span>
            </div>
          </div>
        </div>
        <div className="plans-video-container" ref={videoContainerRef}>
          {shouldLoadVideo && (
            <video
              ref={videoRef}
              autoPlay
              className="plans-video"
              src={planVideos[selectedPlan]}
              poster={planPosters[selectedPlan]}
              loop
              muted
              playsInline
            />
          )}
        </div>
      </div>
    </>
  );
}
